import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import styled from "styled-components";
import { popoverMotion, POPOVER_EXIT_MS } from "../design-system/tokens/popoverMotion";
import { usePresence } from "../hooks/usePresence";
import { Bell } from "react-feather";
import { rawColors } from "../design-system/tokens/colors";
import { spacing } from "../design-system/tokens/spacing";
import { borderRadius } from "../design-system/tokens/borders";
// import { useNotifications } from "../hooks/useNotifications";
import { useNavigate } from "react-router-dom";
import { ActivityList } from "./molecules/ActivityList";
import { useNotifications as useNotificationStore } from "../hooks/useNotifications";
import { clearActivity, closeActivity, openActivity } from "../services/notify";
import { NOTIFICATION_ACTIVITY_COPY as ACTIVITY, toActivityEntries } from "../services/notify/notifyActivity";

const NotificationWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const NotificationBellButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  /* Top-bar spec: a 32px round ghost button. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing[8]};
  height: ${spacing[8]};
  padding: 0;
  margin: 0;
  border-radius: 50%;
  color: ${rawColors.text.mediumEmphasis};

  &:hover {
    background: ${rawColors.primary[100]};
  }
`;

// GAP: no spacing or font-size token for the 14px badge and its 9px count.
const BADGE_SIZE = "0.875rem";
const BADGE_FONT_SIZE = "0.5625rem";

/** Unread badge: a 14px primary count with a white ring (top/right 4px). Without a count it is a plain dot. */
const UnreadBadge = styled.span`
  position: absolute;
  top: ${spacing[1]};
  right: ${spacing[1]};
  min-width: ${BADGE_SIZE};
  height: ${BADGE_SIZE};
  padding: 0 ${spacing[0.5]};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${rawColors.primary[500]};
  color: ${rawColors.text.white};
  font-size: ${BADGE_FONT_SIZE};
  font-weight: 700;
  line-height: 1;
  border-radius: ${borderRadius.full};
  border: 2px solid ${rawColors.structural.white};
  z-index: 2;
`;

const MAX_BADGE_COUNT = 9;

const Tooltip = styled.div`
  position: absolute;
  top: 120%;
  right: 0;
  background: #19213d;
  color: ${rawColors.text.white};
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 14px;
  white-space: pre-line;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  min-width: 180px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s;
`;

const JobsDropdown = styled.div<{ $closing?: boolean }>`
  position: absolute;
  ${({ $closing }) => popoverMotion($closing)}
  transform-origin: top right;
  top: 120%;
  right: 0;
  background: ${rawColors.structural.white};
  color: #222;
  min-width: 300px;
  max-height: 340px;
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  z-index: 1002;
  padding: 0;
  overflow-y: auto;
  border: 1px solid ${rawColors.grey[200]};
`;

interface NotificationsProps {
  story?: { id: string };
}

const Notifications: React.FC<NotificationsProps> = () => {
  const navigate = useNavigate();
  const [showJobsDropdown, setShowJobsDropdown] = useState(false);
  // Round 6: the app-wide notifications' Recent activity lives under this bell too.
  const activity = useNotificationStore();
  const [notification, setNotification] = useState<{ message: string } | null>(
    null
  );
  const [showTooltip, setShowTooltip] = useState(false);
  const jobsDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        jobsDropdownRef.current &&
        !jobsDropdownRef.current.contains(e.target as Node)
      ) {
        setShowJobsDropdown(false);
      }
    };
    if (showJobsDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showJobsDropdown]);

  const stableHandleNotificationClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setShowJobsDropdown((prev) => !prev);
    },
    []
  );

  useEffect(() => {
    const cleanup = () => {
      setShowJobsDropdown(false);
      setShowTooltip(false);
      setNotification(null);
    };

    return cleanup;
  }, []);

  useEffect(() => {
    if (showJobsDropdown) openActivity();
    else closeActivity();
  }, [showJobsDropdown]);

  const runActivityAction = useCallback(
    (id: string) => {
      const action = activity.activity.find((entry) => entry.id === id)?.action;
      setShowJobsDropdown(false);
      action?.onClick?.();
      if (action?.to) navigate(action.to);
    },
    [activity.activity, navigate],
  );

  const { mounted: dropdownMounted } = usePresence(showJobsDropdown, POPOVER_EXIT_MS);

  const dropdownContent = useMemo(() => {
    if (!dropdownMounted) return null;

    return (
      <JobsDropdown ref={jobsDropdownRef} $closing={!showJobsDropdown}>
        <ActivityList
          titleId="recent-activity-title"
          title={ACTIVITY.title}
          entries={toActivityEntries(activity.activity, Date.now())}
          clearLabel={ACTIVITY.clear}
          onClear={clearActivity}
          onAction={runActivityAction}
          newLabel={ACTIVITY.newLabel}
          emptyTitle={ACTIVITY.emptyTitle}
          emptyBody={ACTIVITY.emptyBody}
        />
      </JobsDropdown>
    );
  }, [dropdownMounted, showJobsDropdown, activity.activity, runActivityAction]);

  return (
    <NotificationWrapper>
      <NotificationBellButton
        aria-label={ACTIVITY.bell(activity.unread, activity.unreadHasError)}
        aria-expanded={showJobsDropdown}
        onClick={(e) => stableHandleNotificationClick(e)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <Bell size={18} />
        {activity.unread > 0 && (
          <UnreadBadge aria-hidden="true">
            {activity.unread > 0
              ? activity.unread > MAX_BADGE_COUNT
                ? `${MAX_BADGE_COUNT}+`
                : activity.unread
              : null}
          </UnreadBadge>
        )}
        {notification && showTooltip && (
          <Tooltip>{notification.message}</Tooltip>
        )}
      </NotificationBellButton>
      {dropdownContent}
    </NotificationWrapper>
  );
};

export default React.memo(Notifications, (prev, next) => prev.story?.id === next.story?.id);
